import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { API } from "api";
import { UTILS } from "utils";
import CustomerTable from "../components/CustomerTable";
import { errorHandler } from "api/api/errorHandler";
import { downloadReports } from "utils/Utils";
import { errorNotification } from "utils/Utils";
import AppContainer from "components/ui/AppContainer.jsx";
import AppDatePicker from "components/ui/AppDatePicker.jsx";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton.jsx";
import AppHeading1 from "components/ui/AppHeading1.jsx";
import moment from "moment";

const CustomersReport = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState({
    data: [],
    totalPages: 1,
  });

  const resetstate = () => {
    setCustomers({
      data: [],
      totalPages: 1,
    });
  };

  const toApiDate = (iso) => {
    if (!iso) return "";
    const m = moment(iso, "YYYY-MM-DD", true);
    return m.isValid() ? m.format("DD/MM/YYYY") : "";
  };

  const getCustomerReport = async (pageNumber) => {
    try {
      const formattedStartDate = toApiDate(startDate);
      const formattedEndDate = toApiDate(endDate);
      if (
        !UTILS.validateSelectedDates(formattedStartDate, formattedEndDate)
      ) {
        return;
      }
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.GET_CUSTOMER_REPORT({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        brandId: userDetail.brandId,
        page: pageNumber ?? 1,
      });
      if (result?.success === 1) {
        if (result?.data?.customers && result?.data?.customers.length > 0) {
          const customerReport = await UTILS.getEachCustomeData(
            result?.data?.customers
          );

          setCustomers({
            data: customerReport,
            totalPages: result?.data?.count,
          });
        } else {
          UTILS.handleSuccesNotification(t("No Customer Report Found"));
          resetstate();
        }
      }
    } catch (error) {
      resetstate();
      UTILS.handleErrorNotification(
        t(error?.error?.message || errorHandler(error?.error))
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadexcel = async () => {
    try {
      const formattedStartDate = toApiDate(startDate);
      const formattedEndDate = toApiDate(endDate);
      if (
        !UTILS.validateSelectedDates(formattedStartDate, formattedEndDate)
      ) {
        return;
      }
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.downloadCustomerExcelReport({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        brandId: userDetail.brandId,
      });
      if (result?.success === 1 && result?.data) {
        UTILS.handleSuccesNotification(
          t("Customer Report Fetched Successfully")
        );
        const url = result?.data;
        downloadReports(url);
      } else {
        UTILS.handleErrorNotification(
          t(result?.error?.message || errorHandler(result?.error))
        );
      }
    } catch (error) {
      errorNotification(errorHandler(error?.error?.message || error?.error));
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date && date.isValid() ? date.format("YYYY-MM-DD") : null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date && date.isValid() ? date.format("YYYY-MM-DD") : null);
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    if (!toApiDate(startDate) || !toApiDate(endDate)) return;
    getCustomerReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when range changes; handler reads latest dates.
  }, [startDate, endDate]);

  return (
    <AppContainer
      title={t("Customer Report")}
      subTitle={t(
        "Here you can track all your past orders, earnings, and payment details in one place."
      )}
      loading={loading}
    >
      <div className="flex w-full flex-col gap-3 py-4 md:flex-row md:flex-wrap md:items-end">
        <div className="w-full min-w-0 md:min-w-[18rem] md:w-80 lg:w-96 md:flex-shrink-0">
          <AppDatePicker
            label={t("Start date")}
            value={startDate || undefined}
            onChange={handleStartDateChange}
          />
        </div>
        <div className="w-full min-w-0 md:min-w-[18rem] md:w-80 lg:w-96 md:flex-shrink-0">
          <AppDatePicker
            label={t("End date")}
            value={endDate || undefined}
            onChange={handleEndDateChange}
          />
        </div>
      </div>

      <div className="my-3 flex flex-col md:flex-row min-w-0 flex-wrap items-center justify-between gap-4">
        <AppHeading1>{t("Customers")}</AppHeading1>
        <div className="flex flex-col md:flex-row items-center md:justify-end justify-start gap-3 sm:w-auto md:max-w-2xl">
          <AppSearchInput
            value={searchTerm}
            placeholder={t("Search by name or phone")}
            onInput={(e) => setSearchTerm(e.target.value)}
          />
          {customers?.data?.length > 0 && (
            <AppButton onClick={downloadexcel} type="primary">
              {t("Export")}
            </AppButton>
          )}
        </div>
      </div>
      <CustomerTable customers={customers} searchTerm={searchTerm} />
    </AppContainer>
  );
};

export default CustomersReport;
